import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useUser } from '@src/hooks/useUser';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useExecutionReports } from '@src/providers/ExecutionReportsContext';
import {
  bpsToPercent,
  FormControlSizes,
  HStack,
  logger,
  ModeEnum,
  NotificationVariants,
  SideEnum,
  useCallbackRef,
  useGlobalToasts,
  useSecuritiesContext,
  useSocketClient,
  type Quote,
  type QuoteMarket,
} from '@talos/kyoko';
import { acceptQuote, quoteToAcceptQuoteParams } from '../../../../components/OMS/NewRFQ/acceptQuote';
import { PartialFillButton } from '../../../../components/OMS/NewRFQ/Components/Quotes/PartialFillButton';
import { isQuoteTradeSideDisabled } from '../utils';

interface QuoteMarketPartialFillButtonProps {
  quote: Quote;
  quoteMarket: QuoteMarket;
  careOrderSide?: SideEnum;
}

interface ConfirmTradeParams {
  side: SideEnum;
  price: string;
  orderQty: string;
}

export function QuoteMarketPartialFillButton({ quote, quoteMarket, careOrderSide }: QuoteMarketPartialFillButtonProps) {
  const user = useUser();
  const webSocketClient = useSocketClient();
  const { add: addToast } = useGlobalToasts();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { executionReportSub } = useExecutionReports();
  const { allowedSlippage } = useTradingSettings();
  const { truncateCareOrderID } = useFeatureFlag();

  const handleConfirmTrade = useCallbackRef(({ side, price, orderQty }: ConfirmTradeParams) => {
    const security = securitiesBySymbol.get(quote.Symbol);

    if (!security) {
      logger.error(new Error('Failed to build partial-fill accept quote params - missing security'), {
        extra: { RFQID: quote.RFQID, Symbol: quote.Symbol },
      });
      addToast({
        text: 'An error occurred while accepting the quote. Please try again.',
        variant: NotificationVariants.Negative,
        dismissable: true,
        timeout: 5000,
      });
      return;
    }

    const params = quoteToAcceptQuoteParams({
      quote,
      childQuote: quoteMarket,
      side,
      price,
      tradeBest: false,
      allowedSlippage: allowedSlippage?.value ? bpsToPercent(allowedSlippage.value) : undefined,
      orderQty,
      truncateCareOrderID,
      security,
    });

    if (!params) {
      logger.error(new Error('Failed to build partial-fill accept quote params'), {
        extra: { RFQID: quote.RFQID, Symbol: quote.Symbol },
      });
      addToast({
        text: 'An error occurred while accepting the quote. Please try again.',
        variant: NotificationVariants.Negative,
        dismissable: true,
        timeout: 5000,
      });
      return;
    }

    const handleAcceptQuoteFailure = (result: { text: string; variant?: NotificationVariants }) => {
      logger.error(new Error('acceptQuote failed'), {
        extra: { RFQID: quote.RFQID, Symbol: quote.Symbol, side },
      });
      addToast({
        text: result.text,
        variant: result.variant ?? NotificationVariants.Negative,
        dismissable: true,
        timeout: 5000,
      });
    };

    acceptQuote(params, {
      webSocketClient,
      executionReportsSub: executionReportSub,
    }).catch(handleAcceptQuoteFailure);
  });

  if (quote.NegotiationParameters?.PartialFill !== ModeEnum.Enabled) {
    return null;
  }

  const sellDisabled = isQuoteTradeSideDisabled({
    quote,
    quoteMarket,
    side: SideEnum.Sell,
    user,
    careOrderSide,
  });
  const buyDisabled = isQuoteTradeSideDisabled({
    quote,
    quoteMarket,
    side: SideEnum.Buy,
    user,
    careOrderSide,
  });

  return (
    <HStack gap="spacing4" w="100%" justifyContent="flex-end">
      <PartialFillButton
        quote={quote}
        quoteMarket={quoteMarket}
        size={FormControlSizes.Small}
        buyDisabled={buyDisabled}
        sellDisabled={sellDisabled}
        onConfirmTrade={handleConfirmTrade}
      />
    </HStack>
  );
}
